import { createRoot } from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import App from "./App";
import "./index.css";
import "./lib/i18n";
import { bindNotificationTapNavigation } from "./lib/notification-navigation";

// Register the service worker (vite-plugin-pwa). autoUpdate reloads the app as
// soon as a new worker activates. Browsers only look for a new sw.js on a full
// navigation (or once a day), and an installed PWA resumed from the home screen
// never navigates — so we ask for an update check ourselves: on a timer, when
// the app comes back to the foreground, and when the network returns.
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;
const UPDATE_CHECK_MIN_GAP_MS = 60 * 1000;

registerSW({
  immediate: true,
  onRegisteredSW(swUrl, registration) {
    if (!registration) return;
    let lastCheck = 0;

    const checkForUpdate = async () => {
      const now = Date.now();
      if (now - lastCheck < UPDATE_CHECK_MIN_GAP_MS) return;
      if (registration.installing || !navigator.onLine) return;
      lastCheck = now;
      try {
        // Make sure the server is actually reachable before asking the browser
        // to re-fetch the worker; avoids noisy failures on captive portals.
        const response = await fetch(swUrl, {
          cache: "no-store",
          headers: { "cache-control": "no-cache" },
        });
        if (response.status === 200) await registration.update();
      } catch {
        // Offline or server unreachable — try again on the next trigger.
      }
    };

    setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") void checkForUpdate();
    });
    window.addEventListener("online", () => void checkForUpdate());
  },
  onRegisterError(error) {
    console.error("[PWA] service worker registration failed:", error);
  },
});

createRoot(document.getElementById("root")!).render(<App />);

// Route notification taps to their order (message from the FCM worker for a
// running app, stashed intent for a cold start). Bound immediately — the tap
// may be the very reason the app is opening.
bindNotificationTapNavigation();

// Register for web push notifications once auth has had a chance to restore.
// Lazy-imported so the (large) firebase bundle only loads when needed.
window.addEventListener("load", () => {
  setTimeout(async () => {
    // Only attempt push registration if the user is logged in.
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const { registerPushNotifications } = await import(
        "./lib/firebase-messaging"
      );
      await registerPushNotifications();
    } catch (error) {
      console.error("[PWA] push registration failed:", error);
    }
  }, 1500);
});
